import { isQuilterSpecific } from "@/common/question-set";
import {
  INPROGRESS,
  PROCEEDED,
  PRODUCT,
  QUICKQUOTERETRIEVED,
  QUOTESRETRIEVED,
  SUBMITTED,
  UNABLETOQUOTE,
} from "@/constants/common.constants";
import {
  HOME_LINK,
  LANDLORD_LINK,
} from "@/constants/d2c-landing-page.constants";
import {
  HOME,
  LANDLORDS,
  MPPI,
  TENANTS_CONTENTS,
  TENANTS_LIABILITY,
  RENT_PROTECTION,
} from "@/constants/questionset.constants";
import { SYSTEM_ERRORS } from "@/constants/message.constants";
import { ROUTE_NAME, ROUTE_URL } from "@/constants/route.constants";
import { ApplicationStore, QuestionStore } from "@/store/store";
import router from "@/router";

export const isNavigatingBack = (urlParams, quoteRequestId) => {
  // If there is no QuoteRequestId and no quoteAccessId in the URL but there is one in store,
  // then the user is navigating back to the Cover Options screen
  return (
    !urlParams.quoteAccessId && !urlParams.quoteRequestId && quoteRequestId
  );
};

export const credentialsUnavailable = (urlParams, hasCredentials) => {
  return (
    !hasCredentials &&
    (!urlParams.userId || !urlParams.token) &&
    !urlParams.quoteAccessId
  );
};

const isHomeJourneyRoute = (route) =>
  [ROUTE_NAME.HOME.D2C.LANDING_PAGE, ROUTE_NAME.CONTACT_US].includes(
    route.name
  );

/**
 * Generate contact us link based on product and journey
 * @returns {import("vue-router").Location} vue router location
 */
export const generateLinkContactUs = (isCallChannelIsSelfServe, route) => {
  const isD2CUrl = route.path.includes(ROUTE_URL.D2C);
  const isHomeInsuranceJourney = isHomeJourneyRoute(route);
  if (isCallChannelIsSelfServe || isHomeInsuranceJourney || isD2CUrl) {
    const contactUsRoute = {
      name: ROUTE_NAME.CONTACT_US,
    };
    return contactUsRoute;
  }
  return {
    path: HOME_LINK.CONTACT_US,
  };
};

export const generateLinkHome = (isCallChannelIsSelfServe, route) => {
  const previousUrl = document.referrer;
  const isD2CLandLordUrl =
    route.path.includes(ROUTE_URL.LANDLORD_D2C) ||
    previousUrl.includes(ROUTE_URL.LANDLORD_D2C);
  const isHomeInsuranceJourney = isHomeJourneyRoute(route);
  const leadId = route.query.leadId || getParamFromPreviousUrl("leadId");

  if (isCallChannelIsSelfServe || isHomeInsuranceJourney || isD2CLandLordUrl) {
    const homePageRoute = {
      name: isD2CLandLordUrl
        ? ROUTE_NAME.LANDLORDS.D2C.LANDING_PAGE
        : ROUTE_NAME.HOME.D2C.LANDING_PAGE,
    };
    if (leadId) {
      homePageRoute.query = { leadId };
    }
    return homePageRoute;
  }
  return process.env.VUE_APP_PSL_AH_URL;
};

const faqLinkMap = {
  [PRODUCT.LandLord]: LANDLORD_LINK.FAQS,
  [PRODUCT.Home]: HOME_LINK.FAQS,
};
export const generateFAQSLink = () => {
  const productId = QuestionStore.getters("productId");
  const link = productId
    ? faqLinkMap[productId] ?? faqLinkMap[PRODUCT.Home]
    : faqLinkMap[PRODUCT.Home];
  return link;
};

export const getParamFromPreviousUrl = (paramName) => {
  if (!document.referrer) return "";
  const referrerUrl = new URL(document.referrer);
  const params = new URLSearchParams(referrerUrl.search);
  return params.get(paramName);
};

export const routingToCorrectJourney = async (to, _from, next) => {
  const urlParams = extractUrlParams(to.query);
  // If there are no credentials in store or URL - impossible to proceed
  if (
    credentialsUnavailable(urlParams, QuestionStore.getters("hasCredentials"))
  ) {
    await QuestionStore.dispatch("exitToAdviserHub");
    return;
  }

  // No further processing required if user is just navigating back to CoverOptions
  if (isNavigatingBack(urlParams, QuestionStore.getters("getQuoteRequestId"))) {
    next();
    return;
  }

  QuestionStore.dispatch("toggleAppOverlay", true);

  try {
    await QuestionStore.dispatch("initQuoteAndBuy", urlParams);
  } catch (error) {
    const systemError = SYSTEM_ERRORS[error.message];

    if (systemError) {
      QuestionStore.dispatch("toggleAppOverlay", false);
      router.push({
        name: "ErrorPage",
        params: {
          errorId: systemError.errorId,
          errorTitle: systemError.errorTitle,
          errorMessage: systemError.errorMessage,
          redirectPath: systemError.redirectPath,
        },
      });
      return;
    }
    throw error;
  }

  QuestionStore.dispatch("toggleAppOverlay", false);
  redirectToCorrectJourney(to.query, next);
};

export const shouldGoToStart = (quoteResponse) => {
  const isOriginalQuickQuote =
    QuestionStore.state("isQuickQuote") &&
    quoteResponse.QuickQuoteStatus === QUICKQUOTERETRIEVED;
  const isAnnualRequote = QuestionStore.getters("getIsAnnualRequote");
  const isQuoteExpired = QuestionStore.state("isExpired");

  return isAnnualRequote || isOriginalQuickQuote || isQuoteExpired;
};

// Returns a RouteRecordInfo to be passed to next()
export const getCurrentStepRoute = (
  typeOfSale,
  productGroup,
  firstStep = "COVER_TYPE"
) => {
  const qr = QuestionStore.state("quoteResponse");
  const qm = QuestionStore.state("questionMap");

  const journeyStub = ROUTE_NAME[productGroup][typeOfSale];

  const route = {
    name: null,
    query: null,
  };
  const utmParams = {
    utm_source: qr.ExternalSystemId,
    utm_medium: "partial_integration",
  };

  if (shouldGoToStart(qr)) {
    if (QuestionStore.state("isQuickQuote")) {
      route.query = utmParams;
    }

    if (isQuilterSpecific(QuestionStore.getters("qsId"))) {
      route.name = journeyStub.FACT_FIND;
    } else {
      route.name = journeyStub[firstStep];
    }

    return route;
  }

  const coverTypeAnswered = !!qm?.CoverType?.Value;

  // FactFind journey
  if (isQuilterSpecific(QuestionStore.getters("qsId"))) {
    if (!coverTypeAnswered) {
      return { name: journeyStub.FACT_FIND };
    }
  }

  if (shouldGoToStart(qr)) {
    return { name: journeyStub.COVER_TYPE };
  }

  if (qr.QuickQuoteStatus === PROCEEDED) {
    if (coverTypeAnswered) {
      route.name = journeyStub.YOUR_DETAILS;
      route.query = utmParams;
    } else {
      route.name = journeyStub[firstStep];
    }
    return route;
  }

  // Check if any answers are ThirdParty and go to earliest stage
  const thirdPartyAnswer = qr.Answers.find((a) => a.Source === "ThirdParty");
  if (thirdPartyAnswer) {
    return { name: journeyStub.YOUR_DETAILS };
  }

  switch (qr.QuoteStatus) {
    case INPROGRESS:
      return coverTypeAnswered
        ? { name: journeyStub.YOUR_DETAILS }
        : { name: journeyStub[firstStep] };
    case UNABLETOQUOTE:
      return typeOfSale === "B2B" && productGroup === "HOME"
        ? { name: journeyStub.PROPERTY_DETAILS }
        : { name: journeyStub.QUOTATION_RESULT };
    case QUOTESRETRIEVED:
      if (ApplicationStore.getters("quoteId")) {
        route.name = journeyStub.APPLICATION;
      } else if (QuestionStore.state("isQuickQuote")) {
        route.name = journeyStub.QUOTATION_RESULT;
        route.query = utmParams;
      } else {
        route.name = journeyStub.QUOTATION_RESULT;
      }
      return route;
    case SUBMITTED:
      return {
        name: journeyStub.COMPLETE,
        params: {
          type: "resumeQuote",
          quoteRequestId: qr.QuoteRequestId,
        },
      };
    default:
      return { name: journeyStub.YOUR_DETAILS };
  }
};

export const extractUrlParams = (query) => {
  const urlParams = {
    userId: +query.userId,
    token: query.token,
    branchNumber: query.branchNumber,
    callChannel: query.callChannelType,
    questionSetId: +query.qs,
    quoteRequestId: query.quoteRequestId,
    quoteAccessId: query.quoteAccessId,
    isExpired: query.isExpired,
    isAnnualRequote: query.isAnnualRequote,
    isEssentialCover: query.isEssentialCover,
  };
  return urlParams;
};

const redirectToCorrectJourney = (routeQuery, next) => {
  const stub = getRouteObject();
  /** Temporary route for home main, will be refactored later */
  if (!stub?.MAIN) {
    next();
    return;
  }
  next({ name: stub.MAIN, query: routeQuery });
};

export const getNamePagePaymentByJourney = () => {
  const stub = getRouteObject();
  return stub.PAYMENT ?? "";
};

export const getNamePageSuccessByJourney = () => {
  const stub = getRouteObject();
  return stub.COMPLETE ?? "";
};

const productsMap = {
  [PRODUCT.LandLord]: LANDLORDS,
  [PRODUCT.Tenants_Contents]: TENANTS_CONTENTS,
  [PRODUCT.Tenants_Liability]: TENANTS_LIABILITY,
  [PRODUCT.MPPI]: MPPI,
  [PRODUCT.Home]: HOME,
  [PRODUCT.HomeOld]: HOME,
  [PRODUCT.Rent_Protection]: RENT_PROTECTION,
};

export const getRouteObject = () => {
  const productId = QuestionStore.getters("productId");
  const journey = QuestionStore.getters("isSelfServe") ? "D2C" : "B2B";
  const product = productsMap[productId];
  return ROUTE_NAME[product][journey];
};

const landingPageMap = {
  [PRODUCT.LandLord]: ROUTE_URL.LANDLORD_D2C_LANDINGPAGE,
  [PRODUCT.Home]: ROUTE_URL.HOME_D2C_LANDINGPAGE,
};

export const getLandingPageLink = () => {
  const productId = QuestionStore.getters("productId");
  const landingPage = productId
    ? landingPageMap[productId] ?? landingPageMap[PRODUCT.Home]
    : landingPageMap[PRODUCT.Home];
  return landingPage;
};

export const PRIVACY_POLICY_LINK =
  "https://www.paymentshield.co.uk/fair-processing-notice";
